// Keeps the batches' Microsoft 365 groups in step (Microsoft Graph, with the admin's own permission):
// adds students whose access is on, takes out those who shouldn't be in a group any more.
// The database says what to change (admin_group_changes) and records what happened (admin_group_result).
import { AppError } from './api.js';

const GRAPH = 'https://graph.microsoft.com/v1.0';
const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const GROUP_FIELDS = 'id,displayName,groupTypes,mailEnabled,securityEnabled';
const SAVE_EVERY = 20;
const wait = (ms) => new Promise((r) => setTimeout(r, ms));
const keyOf = (name) => String(name || '').replace(/\s+/g, ' ').trim().toLowerCase();
const quoted = (text) => `'${String(text).replace(/'/g, "''")}'`;

async function call(token, method, path, body) {
  for (let attempt = 0; attempt < 4; attempt++) {
    let res;
    try {
      res = await fetch(GRAPH + path, {
        method,
        headers: { Authorization: `Bearer ${token}`, ...(body ? { 'Content-Type': 'application/json' } : {}) },
        body: body ? JSON.stringify(body) : undefined,
      });
    } catch (e) {
      throw new AppError('Could not reach Microsoft. Check your connection and try again.', 'offline');
    }
    if (res.status === 401) throw new AppError('Microsoft asked to sign in again. Tap the button to allow group changes again.', 'graph_auth', 401);
    if (res.status === 429 || res.status === 503 || res.status === 504) {
      await wait(Math.min(Number(res.headers.get('Retry-After')) || 2, 20) * 1000);
      continue;
    }
    const data = res.status === 204 ? null : await res.json().catch(() => null);
    return { status: res.status, data, message: (data && data.error && data.error.message) || '' };
  }
  throw new AppError('Microsoft is busy right now. Try again in a minute.', 'graph_busy', 429);
}

const ADMIN_ACCOUNT = 'the Microsoft 365 Global Administrator account';
const NO_LOOKUP = `Microsoft didn’t let the app look up groups. Sign in to the app with ${ADMIN_ACCOUNT}, tap the button again and allow group changes when Microsoft asks.`;
const cantChange = (group) => `Your Microsoft account isn’t allowed to change “${group}”. Sign in to the app with ${ADMIN_ACCOUNT} and update again.`;

// Why the app can't change a group's members, or null if it can.
function unusable(g, name) {
  const types = g.groupTypes || [];
  if (types.includes('DynamicMembership')) return `“${name}” chooses its members by a rule, so they can’t be changed by hand.`;
  if (!types.includes('Unified') && g.mailEnabled) {
    return `“${name}” is a distribution list or mail-enabled security group, which apps can’t change. Use a Microsoft 365 group, or make the change by hand.`;
  }
  if (!GUID.test(g.id || '')) return `Microsoft returned an unexpected id for “${name}”.`;
  return null;
}

async function groupById(token, id) {
  const r = await call(token, 'GET', `/groups/${id}?$select=${GROUP_FIELDS}`);
  if (r.status === 200 && r.data) return r.data;
  if (r.status === 404) return null;
  if (r.status === 403) throw new AppError(NO_LOOKUP, 'graph_forbidden', 403);
  throw new AppError(`Microsoft returned an error (${r.status}) looking up a group. Try again in a minute.`, 'graph_error', r.status);
}

async function groupsByName(token, key) {
  const r = await call(token, 'GET', `/groups?$filter=${encodeURIComponent(`displayName eq ${quoted(key)}`)}&$select=${GROUP_FIELDS}`);
  if (r.status === 403) throw new AppError(NO_LOOKUP, 'graph_forbidden', 403);
  if (r.status !== 200) throw new AppError(`Microsoft returned an error (${r.status}) looking up “${key}”. Try again in a minute.`, 'graph_error', r.status);
  return ((r.data && r.data.value) || []).filter((g) => keyOf(g.displayName) === key);
}

// Each group is looked up once per update, and checked against Microsoft 365 as it is now. Groups are
// matched by key: the name with capitals and extra spaces ignored.
function groupFinder(token) {
  const ids = new Map();
  const keys = new Map();
  const byId = (id) => {
    if (!GUID.test(id || '')) return Promise.resolve(null);
    if (!ids.has(id)) ids.set(id, groupById(token, id));
    return ids.get(id);
  };
  const byKey = (key) => {
    if (!keys.has(key)) keys.set(key, groupsByName(token, key));
    return keys.get(key);
  };
  return {
    // Adding: the group the batch names. An id found before is used only while that group still has the name.
    async forAdding(key, name, knownIds) {
      let renamedTo = null;
      for (const id of new Set(knownIds)) {
        const g = await byId(id);
        if (!g) continue;
        if (keyOf(g.displayName) === key) return { id: g.id, error: unusable(g, name) };
        renamedTo = renamedTo || g.displayName;
      }
      const list = await byKey(key);
      if (list.length === 1) return { id: list[0].id, error: unusable(list[0], name), found: true };
      if (list.length > 1) return { error: `More than one Microsoft 365 group is called “${name}”. Rename one of them in Microsoft 365, then update again.` };
      if (renamedTo) return { error: `“${name}” is now called “${renamedTo}” in Microsoft 365. Put the new name in the batch (Edit batch), then update again.` };
      return { error: `No Microsoft 365 group is called “${name}”. Check the batch’s Members group (Edit batch).` };
    },
    // Taking out: the group the app put them in (by its id) while it still has the name the app knows it
    // by; a group with another name now comes back as renamedTo, for the admin to decide. Otherwise the one
    // group with that name.
    async forRemoving(key, name, id) {
      const known = await byId(id);
      if (known) {
        if (keyOf(known.displayName) === key) return { id: known.id, error: unusable(known, name) };
        return { id: known.id, renamedTo: known.displayName || '(no name)' };
      }
      const list = await byKey(key);
      if (list.length === 1) return { id: list[0].id, error: unusable(list[0], name) };
      if (list.length > 1) return { error: `More than one Microsoft 365 group is called “${name}”, so the app can’t tell which to take them out of. Do it by hand, then tap Done by hand.` };
      if (GUID.test(id || '')) return { gone: true }; // the group the app put them in no longer exists
      return { error: `No Microsoft 365 group is called “${name}” now, so the app can’t tell which group to take them out of. If they are in a group they shouldn’t be in, take them out by hand, then tap Done by hand.` };
    },
  };
}

const REFUSED_LOOKUP = { error: 'Microsoft didn’t let the app look up accounts. Allow group changes when Microsoft asks.' };

// The person's Microsoft 365 account: by sign-in name, or failing that by email address.
async function findUser(token, email) {
  let r = await call(token, 'GET', `/users/${encodeURIComponent(email)}?$select=id`);
  if (r.status === 200 && r.data && GUID.test(r.data.id || '')) return { id: r.data.id };
  if (r.status === 403) return REFUSED_LOOKUP;
  if (r.status !== 404) return { error: `Microsoft returned an error (${r.status}) looking up ${email}.` };
  r = await call(token, 'GET', `/users?$filter=${encodeURIComponent(`mail eq ${quoted(email)}`)}&$select=id`);
  if (r.status === 403) return REFUSED_LOOKUP;
  if (r.status !== 200) return { error: `Microsoft returned an error (${r.status}) looking up ${email}.` };
  const found = ((r.data && r.data.value) || []).filter((u) => GUID.test(u.id || ''));
  if (found.length > 1) return { error: `More than one Microsoft 365 account has the email ${email}.` };
  return found.length ? { id: found[0].id } : { missing: true };
}

// Whether the account the app put someone in with is still theirs: its sign-in name or email is theirs.
async function stillTheirs(token, userId, email) {
  const r = await call(token, 'GET', `/users/${userId}?$select=id,userPrincipalName,mail`);
  if (r.status === 403) return REFUSED_LOOKUP;
  if (r.status === 200 && r.data) {
    return { ok: [r.data.userPrincipalName, r.data.mail].some((v) => String(v || '').toLowerCase() === email.toLowerCase()) };
  }
  if (r.status === 404) return { ok: false };
  return { error: `Microsoft returned an error (${r.status}) looking up ${email}.` };
}

// definite: Microsoft surely didn't add them (it refused the request), as opposed to an error that leaves
// it unclear.
async function addMember(token, groupId, userId, groupName) {
  const r = await call(token, 'POST', `/groups/${groupId}/members/$ref`, { '@odata.id': `${GRAPH}/directoryObjects/${userId}` });
  if (r.status === 204) return { ok: true };
  if (r.status === 400 && /already exist/i.test(r.message)) return { ok: true };
  const definite = r.status >= 400 && r.status < 500;
  if (r.status === 403) return { error: cantChange(groupName), definite };
  return { error: `Microsoft returned an error (${r.status}) adding them to “${groupName}”.`, definite };
}

// Always .../members/{id}/$ref: that takes the person out of the group and nothing else (without $ref,
// Microsoft would delete the account itself). The group and the account were both checked just before, so
// "not found" here means that account wasn't in the group.
async function removeMember(token, groupId, userId, groupName) {
  if (!GUID.test(groupId || '') || !GUID.test(userId || '')) return { error: 'The app couldn’t tell which group or account to change.' };
  const r = await call(token, 'DELETE', `/groups/${groupId}/members/${userId}/$ref`);
  if (r.status === 204 || r.status === 404) return { ok: true };
  if (r.status === 403) return { error: cantChange(groupName) };
  return { error: `Microsoft returned an error (${r.status}) taking them out of “${groupName}”.` };
}

// Make the changes. `changes` is what admin_group_changes returned; `only(item, action)` can limit them
// (say, to the students just added to one batch). Each add is noted before Microsoft is asked, and results
// are saved every few people, so an update cut short leaves nothing unknown. Returns the database's new
// list and a summary.
export async function applyGroupChanges(token, changes, rpc, { only = () => true, onProgress = () => {} } = {}) {
  const work = [
    ...changes.adds.filter((x) => only(x, 'add')).map((x) => ({ ...x, action: 'add' })),
    ...changes.removes.filter((x) => only(x, 'remove')).map((x) => ({ ...x, action: 'remove' })),
  ];
  const summary = { added: 0, removed: 0, problems: [] };
  if (!work.length) return { changes, summary };
  const pairKey = (personId, key) => `${personId}|${key}`;

  // The groups to add people to, each found once; ids found by name are kept for next time.
  const finder = groupFinder(token);
  const adding = new Map();
  for (const x of work.filter((w) => w.action === 'add')) {
    const g = adding.get(x.group.key) || { name: x.group.name, ids: [] };
    if (x.group.id) g.ids.push(x.group.id);
    adding.set(x.group.key, g);
  }
  const found = [];
  for (const [key, g] of adding) {
    onProgress({ phase: 'groups', name: g.name });
    g.target = await finder.forAdding(key, g.name, g.ids);
    if (g.target.found && !g.target.error) found.push({ name: g.name, id: g.target.id });
  }
  if (found.length) await rpc('admin_set_group_ids', { p_groups: found });

  // Note the adds about to be made; only those the database still wants are made.
  const ready = new Map(); // person|key -> this update's token
  const toNote = work.filter((x) => x.action === 'add' && !adding.get(x.group.key).target.error)
    .map((x) => ({ person_id: x.person.id, group_key: x.group.key }));
  for (let i = 0; i < toNote.length; i += 500) {
    const res = await rpc('admin_group_intent', { p_items: toNote.slice(i, i + 500) });
    for (const r of res.ready || []) ready.set(pairKey(r.person_id, r.group_key), res.token);
  }

  // The groups each person should be in: never taken out of those, whatever record is being tidied.
  const shouldBeIn = new Map(); // person -> group ids of this update's adds for them
  const addedTo = new Map(); // person -> group ids this update added them to (or found them in)
  const remember = (map, personId, id) => map.set(personId, (map.get(personId) || new Set()).add(id));
  for (const x of work.filter((w) => w.action === 'add')) {
    const t = adding.get(x.group.key).target;
    if (t.id && ready.has(pairKey(x.person.id, x.group.key))) remember(shouldBeIn, x.person.id, t.id);
  }

  const users = new Map();
  const user = async (email) => {
    if (!users.has(email)) users.set(email, await findUser(token, email));
    return users.get(email);
  };
  const add = async (x) => {
    const t = adding.get(x.group.key).target;
    if (t.error) return { error: t.error };
    const noted = ready.get(pairKey(x.person.id, x.group.key));
    if (!noted) return { skip: true }; // no longer to be added: the next list says what to do
    const u = await user(x.person.email);
    if (u.error) return { error: u.error, noted, definite: true };
    if (u.missing) return { error: `There is no Microsoft 365 account ${x.person.email}. Check the student’s academy email.`, noted, definite: true };
    const out = await addMember(token, t.id, u.id, x.group.name);
    if (out.ok) remember(addedTo, x.person.id, t.id);
    return { ...out, noted, groupId: t.id, userId: u.id };
  };
  const remove = async (x) => {
    const t = await finder.forRemoving(x.group.key, x.group.name, x.group.id);
    if (t.error) return { error: t.error };
    if (t.gone) return { ok: true };
    // The same group under its new name, where the app has them: only the record changes.
    if ((x.keep_ids || []).includes(t.id) || (addedTo.get(x.person.id) || new Set()).has(t.id)) return { ok: true, kept: true };
    // They should be in it, but adding them didn't work this time: left for the next update.
    if ((shouldBeIn.get(x.person.id) || new Set()).has(t.id)) return { skip: true };
    if (t.renamedTo) {
      return { error: `“${x.group.name}” is now called “${t.renamedTo}” in Microsoft 365, so the app didn’t change it. If they shouldn’t be in “${t.renamedTo}”, take them out by hand, then tap Done by hand.` };
    }
    let userId = null;
    if (GUID.test(x.user_id || '')) {
      const check = await stillTheirs(token, x.user_id, x.person.email);
      if (check.error) return { error: check.error };
      if (check.ok) userId = x.user_id;
    }
    if (!userId) {
      const u = await user(x.person.email);
      if (u.error) return { error: u.error };
      if (u.missing) {
        return { error: `There is no Microsoft 365 account ${x.person.email} now, so the app can’t tell who to take out of “${x.group.name}”. If they are still in it under another account, take them out by hand, then tap Done by hand.` };
      }
      userId = u.id;
    }
    return { ...(await removeMember(token, t.id, userId, x.group.name)), groupId: t.id };
  };

  let pending = [];
  let latest = changes;
  const save = async () => {
    if (!pending.length) return;
    const batch = pending;
    pending = [];
    try {
      latest = await rpc('admin_group_result', { p_results: batch });
    } catch (e) {
      pending = batch.concat(pending); // tried once more at the end
      throw e;
    }
  };
  let failure = null;
  try {
    for (let i = 0; i < work.length; i++) {
      const x = work[i];
      onProgress({ phase: 'people', done: i, total: work.length, name: x.person.name });
      const out = x.action === 'add' ? await add(x) : await remove(x);
      if (out.skip) continue;
      if (out.ok && !out.kept) summary[x.action === 'add' ? 'added' : 'removed'] += 1;
      if (!out.ok) summary.problems.push({ name: x.person.name, group: x.group.name, action: x.action, error: out.error });
      pending.push({
        person_id: x.person.id, group_key: x.group.key, group_name: x.group.name,
        group_id: (out.ok && out.groupId) || null, user_id: (out.ok && out.userId) || null,
        action: x.action, ok: Boolean(out.ok), error: out.error || null,
        noted: out.noted || null, definite: Boolean(out.definite),
      });
      if (pending.length >= SAVE_EVERY) await save();
    }
  } catch (e) {
    failure = e;
  }
  // Even if Microsoft stopped the update part way, keep the record of what was done.
  onProgress({ phase: 'saving' });
  try { await save(); } catch (e) { failure = failure || e; }
  if (failure) throw failure;
  if (!pending.length && latest === changes) latest = await rpc('admin_group_changes');
  return { changes: latest, summary };
}
